import { useCallback } from 'react'
import { useParams } from 'react-router'
import events from '~/features/GrowthEvent'
import { useMe } from '~/services/helpers/user'
import dayjs from '~/utils/dayjs'
import useNow from '~/utils/hooks/useNow'
import {
  type DynamicGrowthEvents,
  useGrowthEventDynamicConfig,
} from '../growthEventConfig'

/**
 * Hook to get event config and basic state via current route or event ID.
 */
export function useGrowthEventState(eventId?: keyof DynamicGrowthEvents) {
  const { eventRoute } = useParams()
  const currentEvent = eventId
    ? events.find(it => it.id === eventId)
    : eventRoute
      ? events.find(it => it.eventRoute === eventRoute)
      : undefined

  const { beingAdmin, loading: loadingMe } = useMe()
  const { data: dynConfig, isLoading: loadingDynConfig } =
    useGrowthEventDynamicConfig()
  const {
    enabled = false,
    enabledForAdmin = false,
    startDate,
    endDate,
  } = (currentEvent && dynConfig?.[currentEvent.id]) ?? {}

  const isOngoing = useNow(
    useCallback(
      _now => {
        const now = dayjs(_now)
        // A growth event must have definite start & end dates
        const isStarted = Boolean(startDate) && now.isSameOrAfter(startDate)
        const isEnded = !endDate || now.isSameOrAfter(endDate)
        return isStarted && !isEnded
      },
      [startDate, endDate],
    ),
  )

  return {
    ...currentEvent,
    /**
     * Whether it is still loading necessary data to determine the state.
     */
    isLoading: Boolean(currentEvent) && (loadingMe || loadingDynConfig),
    /**
     * Whether the event is effectively enabled/activated with `enabledForAdmin`
     * being considered.
     */
    isEnabled: (enabled || (beingAdmin && enabledForAdmin)) && isOngoing,
  }
}
